// Shared country reference data + flag helpers for display formats.
//
// Used by the `telephone` format (derive the country flag from the leading
// dial code, e.g. +91… → 🇮🇳) and the `country` format (resolve a stored
// value — ISO-2, ISO-3, dial code or country name — to a flag + name).
//
// Flags are rendered as Unicode regional-indicator emoji derived from the
// ISO-3166-1 alpha-2 code, so no image assets are needed.
//
// `name` matches the ISO-3166-1 English short name (the form most country
// master tables store, e.g. "Russian Federation", "Korea, Republic of",
// "Viet Nam"). `aliases` carry the common everyday names ("Russia", "South
// Korea", "Vietnam", "UK"…) so values from any source still resolve.
// Matching is case- and diacritic-insensitive.

export interface Country {
  iso2: string        // ISO-3166-1 alpha-2 (uppercase) — drives the flag emoji
  iso3: string        // ISO-3166-1 alpha-3 (uppercase)
  name: string        // ISO-3166-1 English short name
  dial: string        // E.164 calling code WITHOUT the leading '+', e.g. '91'
  aliases?: string[]  // common alternative names
}

// Full ISO-3166-1 table. Where a calling code is shared (e.g. +1, +7), the
// primary country is listed first so dial-code lookups resolve to it.
export const COUNTRIES: Country[] = [
  { iso2: 'US', iso3: 'USA', name: 'United States of America', dial: '1', aliases: ['United States', 'USA', 'US', 'America'] },
  { iso2: 'CA', iso3: 'CAN', name: 'Canada', dial: '1' },
  { iso2: 'RU', iso3: 'RUS', name: 'Russian Federation', dial: '7', aliases: ['Russia'] },
  { iso2: 'KZ', iso3: 'KAZ', name: 'Kazakhstan', dial: '7' },
  { iso2: 'EG', iso3: 'EGY', name: 'Egypt', dial: '20' },
  { iso2: 'ZA', iso3: 'ZAF', name: 'South Africa', dial: '27' },
  { iso2: 'GR', iso3: 'GRC', name: 'Greece', dial: '30' },
  { iso2: 'NL', iso3: 'NLD', name: 'Netherlands, Kingdom of the', dial: '31', aliases: ['Netherlands', 'Holland'] },
  { iso2: 'BE', iso3: 'BEL', name: 'Belgium', dial: '32' },
  { iso2: 'FR', iso3: 'FRA', name: 'France', dial: '33' },
  { iso2: 'ES', iso3: 'ESP', name: 'Spain', dial: '34' },
  { iso2: 'HU', iso3: 'HUN', name: 'Hungary', dial: '36' },
  { iso2: 'IT', iso3: 'ITA', name: 'Italy', dial: '39' },
  { iso2: 'VA', iso3: 'VAT', name: 'Holy See', dial: '39', aliases: ['Vatican City', 'Vatican'] },
  { iso2: 'RO', iso3: 'ROU', name: 'Romania', dial: '40' },
  { iso2: 'CH', iso3: 'CHE', name: 'Switzerland', dial: '41' },
  { iso2: 'AT', iso3: 'AUT', name: 'Austria', dial: '43' },
  { iso2: 'GB', iso3: 'GBR', name: 'United Kingdom of Great Britain and Northern Ireland', dial: '44', aliases: ['United Kingdom', 'UK', 'Great Britain', 'England', 'Britain'] },
  { iso2: 'DK', iso3: 'DNK', name: 'Denmark', dial: '45' },
  { iso2: 'SE', iso3: 'SWE', name: 'Sweden', dial: '46' },
  { iso2: 'NO', iso3: 'NOR', name: 'Norway', dial: '47' },
  { iso2: 'PL', iso3: 'POL', name: 'Poland', dial: '48' },
  { iso2: 'DE', iso3: 'DEU', name: 'Germany', dial: '49' },
  { iso2: 'PE', iso3: 'PER', name: 'Peru', dial: '51' },
  { iso2: 'MX', iso3: 'MEX', name: 'Mexico', dial: '52' },
  { iso2: 'CU', iso3: 'CUB', name: 'Cuba', dial: '53' },
  { iso2: 'AR', iso3: 'ARG', name: 'Argentina', dial: '54' },
  { iso2: 'BR', iso3: 'BRA', name: 'Brazil', dial: '55' },
  { iso2: 'CL', iso3: 'CHL', name: 'Chile', dial: '56' },
  { iso2: 'CO', iso3: 'COL', name: 'Colombia', dial: '57' },
  { iso2: 'VE', iso3: 'VEN', name: 'Venezuela, Bolivarian Republic of', dial: '58', aliases: ['Venezuela'] },
  { iso2: 'MY', iso3: 'MYS', name: 'Malaysia', dial: '60' },
  { iso2: 'AU', iso3: 'AUS', name: 'Australia', dial: '61' },
  { iso2: 'ID', iso3: 'IDN', name: 'Indonesia', dial: '62' },
  { iso2: 'PH', iso3: 'PHL', name: 'Philippines', dial: '63' },
  { iso2: 'NZ', iso3: 'NZL', name: 'New Zealand', dial: '64' },
  { iso2: 'SG', iso3: 'SGP', name: 'Singapore', dial: '65' },
  { iso2: 'TH', iso3: 'THA', name: 'Thailand', dial: '66' },
  { iso2: 'JP', iso3: 'JPN', name: 'Japan', dial: '81' },
  { iso2: 'KR', iso3: 'KOR', name: 'Korea, Republic of', dial: '82', aliases: ['South Korea', 'Korea'] },
  { iso2: 'VN', iso3: 'VNM', name: 'Viet Nam', dial: '84', aliases: ['Vietnam'] },
  { iso2: 'CN', iso3: 'CHN', name: 'China', dial: '86' },
  { iso2: 'TR', iso3: 'TUR', name: 'Türkiye', dial: '90', aliases: ['Turkey', 'Turkiye'] },
  { iso2: 'IN', iso3: 'IND', name: 'India', dial: '91' },
  { iso2: 'PK', iso3: 'PAK', name: 'Pakistan', dial: '92' },
  { iso2: 'AF', iso3: 'AFG', name: 'Afghanistan', dial: '93' },
  { iso2: 'LK', iso3: 'LKA', name: 'Sri Lanka', dial: '94' },
  { iso2: 'MM', iso3: 'MMR', name: 'Myanmar', dial: '95', aliases: ['Burma'] },
  { iso2: 'IR', iso3: 'IRN', name: 'Iran, Islamic Republic of', dial: '98', aliases: ['Iran'] },
  { iso2: 'SS', iso3: 'SSD', name: 'South Sudan', dial: '211' },
  { iso2: 'MA', iso3: 'MAR', name: 'Morocco', dial: '212' },
  { iso2: 'DZ', iso3: 'DZA', name: 'Algeria', dial: '213' },
  { iso2: 'TN', iso3: 'TUN', name: 'Tunisia', dial: '216' },
  { iso2: 'LY', iso3: 'LBY', name: 'Libya', dial: '218' },
  { iso2: 'GM', iso3: 'GMB', name: 'Gambia', dial: '220' },
  { iso2: 'SN', iso3: 'SEN', name: 'Senegal', dial: '221' },
  { iso2: 'MR', iso3: 'MRT', name: 'Mauritania', dial: '222' },
  { iso2: 'ML', iso3: 'MLI', name: 'Mali', dial: '223' },
  { iso2: 'GN', iso3: 'GIN', name: 'Guinea', dial: '224' },
  { iso2: 'CI', iso3: 'CIV', name: "Côte d'Ivoire", dial: '225', aliases: ['Ivory Coast'] },
  { iso2: 'BF', iso3: 'BFA', name: 'Burkina Faso', dial: '226' },
  { iso2: 'NE', iso3: 'NER', name: 'Niger', dial: '227' },
  { iso2: 'TG', iso3: 'TGO', name: 'Togo', dial: '228' },
  { iso2: 'BJ', iso3: 'BEN', name: 'Benin', dial: '229' },
  { iso2: 'MU', iso3: 'MUS', name: 'Mauritius', dial: '230' },
  { iso2: 'LR', iso3: 'LBR', name: 'Liberia', dial: '231' },
  { iso2: 'SL', iso3: 'SLE', name: 'Sierra Leone', dial: '232' },
  { iso2: 'GH', iso3: 'GHA', name: 'Ghana', dial: '233' },
  { iso2: 'NG', iso3: 'NGA', name: 'Nigeria', dial: '234' },
  { iso2: 'TD', iso3: 'TCD', name: 'Chad', dial: '235' },
  { iso2: 'CF', iso3: 'CAF', name: 'Central African Republic', dial: '236' },
  { iso2: 'CM', iso3: 'CMR', name: 'Cameroon', dial: '237' },
  { iso2: 'CV', iso3: 'CPV', name: 'Cabo Verde', dial: '238', aliases: ['Cape Verde'] },
  { iso2: 'ST', iso3: 'STP', name: 'Sao Tome and Principe', dial: '239' },
  { iso2: 'GQ', iso3: 'GNQ', name: 'Equatorial Guinea', dial: '240' },
  { iso2: 'GA', iso3: 'GAB', name: 'Gabon', dial: '241' },
  { iso2: 'CG', iso3: 'COG', name: 'Congo', dial: '242', aliases: ['Republic of the Congo', 'Congo-Brazzaville'] },
  { iso2: 'CD', iso3: 'COD', name: 'Congo, Democratic Republic of the', dial: '243', aliases: ['DR Congo', 'DRC', 'Congo-Kinshasa'] },
  { iso2: 'AO', iso3: 'AGO', name: 'Angola', dial: '244' },
  { iso2: 'GW', iso3: 'GNB', name: 'Guinea-Bissau', dial: '245' },
  { iso2: 'IO', iso3: 'IOT', name: 'British Indian Ocean Territory', dial: '246' },
  { iso2: 'SC', iso3: 'SYC', name: 'Seychelles', dial: '248' },
  { iso2: 'SD', iso3: 'SDN', name: 'Sudan', dial: '249' },
  { iso2: 'RW', iso3: 'RWA', name: 'Rwanda', dial: '250' },
  { iso2: 'ET', iso3: 'ETH', name: 'Ethiopia', dial: '251' },
  { iso2: 'SO', iso3: 'SOM', name: 'Somalia', dial: '252' },
  { iso2: 'DJ', iso3: 'DJI', name: 'Djibouti', dial: '253' },
  { iso2: 'KE', iso3: 'KEN', name: 'Kenya', dial: '254' },
  { iso2: 'TZ', iso3: 'TZA', name: 'Tanzania, United Republic of', dial: '255', aliases: ['Tanzania'] },
  { iso2: 'UG', iso3: 'UGA', name: 'Uganda', dial: '256' },
  { iso2: 'BI', iso3: 'BDI', name: 'Burundi', dial: '257' },
  { iso2: 'MZ', iso3: 'MOZ', name: 'Mozambique', dial: '258' },
  { iso2: 'ZM', iso3: 'ZMB', name: 'Zambia', dial: '260' },
  { iso2: 'MG', iso3: 'MDG', name: 'Madagascar', dial: '261' },
  { iso2: 'RE', iso3: 'REU', name: 'Réunion', dial: '262', aliases: ['Reunion'] },
  { iso2: 'ZW', iso3: 'ZWE', name: 'Zimbabwe', dial: '263' },
  { iso2: 'NA', iso3: 'NAM', name: 'Namibia', dial: '264' },
  { iso2: 'MW', iso3: 'MWI', name: 'Malawi', dial: '265' },
  { iso2: 'LS', iso3: 'LSO', name: 'Lesotho', dial: '266' },
  { iso2: 'BW', iso3: 'BWA', name: 'Botswana', dial: '267' },
  { iso2: 'SZ', iso3: 'SWZ', name: 'Eswatini', dial: '268', aliases: ['Swaziland'] },
  { iso2: 'KM', iso3: 'COM', name: 'Comoros', dial: '269' },
  { iso2: 'SH', iso3: 'SHN', name: 'Saint Helena, Ascension and Tristan da Cunha', dial: '290', aliases: ['Saint Helena'] },
  { iso2: 'ER', iso3: 'ERI', name: 'Eritrea', dial: '291' },
  { iso2: 'AW', iso3: 'ABW', name: 'Aruba', dial: '297' },
  { iso2: 'FO', iso3: 'FRO', name: 'Faroe Islands', dial: '298' },
  { iso2: 'GL', iso3: 'GRL', name: 'Greenland', dial: '299' },
  { iso2: 'GI', iso3: 'GIB', name: 'Gibraltar', dial: '350' },
  { iso2: 'PT', iso3: 'PRT', name: 'Portugal', dial: '351' },
  { iso2: 'LU', iso3: 'LUX', name: 'Luxembourg', dial: '352' },
  { iso2: 'IE', iso3: 'IRL', name: 'Ireland', dial: '353' },
  { iso2: 'IS', iso3: 'ISL', name: 'Iceland', dial: '354' },
  { iso2: 'AL', iso3: 'ALB', name: 'Albania', dial: '355' },
  { iso2: 'MT', iso3: 'MLT', name: 'Malta', dial: '356' },
  { iso2: 'CY', iso3: 'CYP', name: 'Cyprus', dial: '357' },
  { iso2: 'FI', iso3: 'FIN', name: 'Finland', dial: '358' },
  { iso2: 'BG', iso3: 'BGR', name: 'Bulgaria', dial: '359' },
  { iso2: 'LT', iso3: 'LTU', name: 'Lithuania', dial: '370' },
  { iso2: 'LV', iso3: 'LVA', name: 'Latvia', dial: '371' },
  { iso2: 'EE', iso3: 'EST', name: 'Estonia', dial: '372' },
  { iso2: 'MD', iso3: 'MDA', name: 'Moldova, Republic of', dial: '373', aliases: ['Moldova'] },
  { iso2: 'AM', iso3: 'ARM', name: 'Armenia', dial: '374' },
  { iso2: 'BY', iso3: 'BLR', name: 'Belarus', dial: '375' },
  { iso2: 'AD', iso3: 'AND', name: 'Andorra', dial: '376' },
  { iso2: 'MC', iso3: 'MCO', name: 'Monaco', dial: '377' },
  { iso2: 'SM', iso3: 'SMR', name: 'San Marino', dial: '378' },
  { iso2: 'UA', iso3: 'UKR', name: 'Ukraine', dial: '380' },
  { iso2: 'RS', iso3: 'SRB', name: 'Serbia', dial: '381' },
  { iso2: 'ME', iso3: 'MNE', name: 'Montenegro', dial: '382' },
  { iso2: 'XK', iso3: 'XKX', name: 'Kosovo', dial: '383' },
  { iso2: 'HR', iso3: 'HRV', name: 'Croatia', dial: '385' },
  { iso2: 'SI', iso3: 'SVN', name: 'Slovenia', dial: '386' },
  { iso2: 'BA', iso3: 'BIH', name: 'Bosnia and Herzegovina', dial: '387' },
  { iso2: 'MK', iso3: 'MKD', name: 'North Macedonia', dial: '389', aliases: ['Macedonia'] },
  { iso2: 'CZ', iso3: 'CZE', name: 'Czechia', dial: '420', aliases: ['Czech Republic'] },
  { iso2: 'SK', iso3: 'SVK', name: 'Slovakia', dial: '421' },
  { iso2: 'LI', iso3: 'LIE', name: 'Liechtenstein', dial: '423' },
  { iso2: 'FK', iso3: 'FLK', name: 'Falkland Islands (Malvinas)', dial: '500', aliases: ['Falkland Islands'] },
  { iso2: 'BZ', iso3: 'BLZ', name: 'Belize', dial: '501' },
  { iso2: 'GT', iso3: 'GTM', name: 'Guatemala', dial: '502' },
  { iso2: 'SV', iso3: 'SLV', name: 'El Salvador', dial: '503' },
  { iso2: 'HN', iso3: 'HND', name: 'Honduras', dial: '504' },
  { iso2: 'NI', iso3: 'NIC', name: 'Nicaragua', dial: '505' },
  { iso2: 'CR', iso3: 'CRI', name: 'Costa Rica', dial: '506' },
  { iso2: 'PA', iso3: 'PAN', name: 'Panama', dial: '507' },
  { iso2: 'PM', iso3: 'SPM', name: 'Saint Pierre and Miquelon', dial: '508' },
  { iso2: 'HT', iso3: 'HTI', name: 'Haiti', dial: '509' },
  { iso2: 'GP', iso3: 'GLP', name: 'Guadeloupe', dial: '590' },
  { iso2: 'BO', iso3: 'BOL', name: 'Bolivia, Plurinational State of', dial: '591', aliases: ['Bolivia'] },
  { iso2: 'GY', iso3: 'GUY', name: 'Guyana', dial: '592' },
  { iso2: 'EC', iso3: 'ECU', name: 'Ecuador', dial: '593' },
  { iso2: 'GF', iso3: 'GUF', name: 'French Guiana', dial: '594' },
  { iso2: 'PY', iso3: 'PRY', name: 'Paraguay', dial: '595' },
  { iso2: 'MQ', iso3: 'MTQ', name: 'Martinique', dial: '596' },
  { iso2: 'SR', iso3: 'SUR', name: 'Suriname', dial: '597' },
  { iso2: 'UY', iso3: 'URY', name: 'Uruguay', dial: '598' },
  { iso2: 'CW', iso3: 'CUW', name: 'Curaçao', dial: '599', aliases: ['Curacao'] },
  { iso2: 'TL', iso3: 'TLS', name: 'Timor-Leste', dial: '670', aliases: ['East Timor'] },
  { iso2: 'NF', iso3: 'NFK', name: 'Norfolk Island', dial: '672' },
  { iso2: 'BN', iso3: 'BRN', name: 'Brunei Darussalam', dial: '673', aliases: ['Brunei'] },
  { iso2: 'NR', iso3: 'NRU', name: 'Nauru', dial: '674' },
  { iso2: 'PG', iso3: 'PNG', name: 'Papua New Guinea', dial: '675' },
  { iso2: 'TO', iso3: 'TON', name: 'Tonga', dial: '676' },
  { iso2: 'SB', iso3: 'SLB', name: 'Solomon Islands', dial: '677' },
  { iso2: 'VU', iso3: 'VUT', name: 'Vanuatu', dial: '678' },
  { iso2: 'FJ', iso3: 'FJI', name: 'Fiji', dial: '679' },
  { iso2: 'PW', iso3: 'PLW', name: 'Palau', dial: '680' },
  { iso2: 'WF', iso3: 'WLF', name: 'Wallis and Futuna', dial: '681' },
  { iso2: 'CK', iso3: 'COK', name: 'Cook Islands', dial: '682' },
  { iso2: 'NU', iso3: 'NIU', name: 'Niue', dial: '683' },
  { iso2: 'WS', iso3: 'WSM', name: 'Samoa', dial: '685' },
  { iso2: 'KI', iso3: 'KIR', name: 'Kiribati', dial: '686' },
  { iso2: 'NC', iso3: 'NCL', name: 'New Caledonia', dial: '687' },
  { iso2: 'TV', iso3: 'TUV', name: 'Tuvalu', dial: '688' },
  { iso2: 'PF', iso3: 'PYF', name: 'French Polynesia', dial: '689' },
  { iso2: 'TK', iso3: 'TKL', name: 'Tokelau', dial: '690' },
  { iso2: 'FM', iso3: 'FSM', name: 'Micronesia, Federated States of', dial: '691', aliases: ['Micronesia'] },
  { iso2: 'MH', iso3: 'MHL', name: 'Marshall Islands', dial: '692' },
  { iso2: 'KP', iso3: 'PRK', name: "Korea, Democratic People's Republic of", dial: '850', aliases: ['North Korea'] },
  { iso2: 'HK', iso3: 'HKG', name: 'Hong Kong', dial: '852' },
  { iso2: 'MO', iso3: 'MAC', name: 'Macao', dial: '853', aliases: ['Macau'] },
  { iso2: 'KH', iso3: 'KHM', name: 'Cambodia', dial: '855' },
  { iso2: 'LA', iso3: 'LAO', name: "Lao People's Democratic Republic", dial: '856', aliases: ['Laos'] },
  { iso2: 'BD', iso3: 'BGD', name: 'Bangladesh', dial: '880' },
  { iso2: 'TW', iso3: 'TWN', name: 'Taiwan, Province of China', dial: '886', aliases: ['Taiwan'] },
  { iso2: 'MV', iso3: 'MDV', name: 'Maldives', dial: '960' },
  { iso2: 'LB', iso3: 'LBN', name: 'Lebanon', dial: '961' },
  { iso2: 'JO', iso3: 'JOR', name: 'Jordan', dial: '962' },
  { iso2: 'SY', iso3: 'SYR', name: 'Syrian Arab Republic', dial: '963', aliases: ['Syria'] },
  { iso2: 'IQ', iso3: 'IRQ', name: 'Iraq', dial: '964' },
  { iso2: 'KW', iso3: 'KWT', name: 'Kuwait', dial: '965' },
  { iso2: 'SA', iso3: 'SAU', name: 'Saudi Arabia', dial: '966' },
  { iso2: 'YE', iso3: 'YEM', name: 'Yemen', dial: '967' },
  { iso2: 'OM', iso3: 'OMN', name: 'Oman', dial: '968' },
  { iso2: 'PS', iso3: 'PSE', name: 'Palestine, State of', dial: '970', aliases: ['Palestine'] },
  { iso2: 'AE', iso3: 'ARE', name: 'United Arab Emirates', dial: '971', aliases: ['UAE'] },
  { iso2: 'IL', iso3: 'ISR', name: 'Israel', dial: '972' },
  { iso2: 'BH', iso3: 'BHR', name: 'Bahrain', dial: '973' },
  { iso2: 'QA', iso3: 'QAT', name: 'Qatar', dial: '974' },
  { iso2: 'BT', iso3: 'BTN', name: 'Bhutan', dial: '975' },
  { iso2: 'MN', iso3: 'MNG', name: 'Mongolia', dial: '976' },
  { iso2: 'NP', iso3: 'NPL', name: 'Nepal', dial: '977' },
  { iso2: 'TJ', iso3: 'TJK', name: 'Tajikistan', dial: '992' },
  { iso2: 'TM', iso3: 'TKM', name: 'Turkmenistan', dial: '993' },
  { iso2: 'AZ', iso3: 'AZE', name: 'Azerbaijan', dial: '994' },
  { iso2: 'GE', iso3: 'GEO', name: 'Georgia', dial: '995' },
  { iso2: 'KG', iso3: 'KGZ', name: 'Kyrgyzstan', dial: '996' },
  { iso2: 'UZ', iso3: 'UZB', name: 'Uzbekistan', dial: '998' },
  // North American Numbering Plan (+1) territories and Caribbean nations.
  { iso2: 'BS', iso3: 'BHS', name: 'Bahamas', dial: '1242' },
  { iso2: 'BB', iso3: 'BRB', name: 'Barbados', dial: '1246' },
  { iso2: 'AI', iso3: 'AIA', name: 'Anguilla', dial: '1264' },
  { iso2: 'AG', iso3: 'ATG', name: 'Antigua and Barbuda', dial: '1268' },
  { iso2: 'VG', iso3: 'VGB', name: 'Virgin Islands (British)', dial: '1284', aliases: ['British Virgin Islands'] },
  { iso2: 'VI', iso3: 'VIR', name: 'Virgin Islands (U.S.)', dial: '1340', aliases: ['US Virgin Islands'] },
  { iso2: 'KY', iso3: 'CYM', name: 'Cayman Islands', dial: '1345' },
  { iso2: 'BM', iso3: 'BMU', name: 'Bermuda', dial: '1441' },
  { iso2: 'GD', iso3: 'GRD', name: 'Grenada', dial: '1473' },
  { iso2: 'TC', iso3: 'TCA', name: 'Turks and Caicos Islands', dial: '1649' },
  { iso2: 'MS', iso3: 'MSR', name: 'Montserrat', dial: '1664' },
  { iso2: 'MP', iso3: 'MNP', name: 'Northern Mariana Islands', dial: '1670' },
  { iso2: 'GU', iso3: 'GUM', name: 'Guam', dial: '1671' },
  { iso2: 'AS', iso3: 'ASM', name: 'American Samoa', dial: '1684' },
  { iso2: 'SX', iso3: 'SXM', name: 'Sint Maarten (Dutch part)', dial: '1721' },
  { iso2: 'LC', iso3: 'LCA', name: 'Saint Lucia', dial: '1758' },
  { iso2: 'DM', iso3: 'DMA', name: 'Dominica', dial: '1767' },
  { iso2: 'VC', iso3: 'VCT', name: 'Saint Vincent and the Grenadines', dial: '1784' },
  { iso2: 'PR', iso3: 'PRI', name: 'Puerto Rico', dial: '1787' },
  { iso2: 'DO', iso3: 'DOM', name: 'Dominican Republic', dial: '1809' },
  { iso2: 'TT', iso3: 'TTO', name: 'Trinidad and Tobago', dial: '1868' },
  { iso2: 'KN', iso3: 'KNA', name: 'Saint Kitts and Nevis', dial: '1869' },
  { iso2: 'JM', iso3: 'JAM', name: 'Jamaica', dial: '1876' },
  // Remaining ISO-3166-1 entries without a unique short dial code above.
  { iso2: 'AX', iso3: 'ALA', name: 'Åland Islands', dial: '358', aliases: ['Aland Islands'] },
  { iso2: 'AQ', iso3: 'ATA', name: 'Antarctica', dial: '' },
  { iso2: 'BV', iso3: 'BVT', name: 'Bouvet Island', dial: '' },
  { iso2: 'CX', iso3: 'CXR', name: 'Christmas Island', dial: '61' },
  { iso2: 'CC', iso3: 'CCK', name: 'Cocos (Keeling) Islands', dial: '61' },
  { iso2: 'TF', iso3: 'ATF', name: 'French Southern Territories', dial: '' },
  { iso2: 'GG', iso3: 'GGY', name: 'Guernsey', dial: '44' },
  { iso2: 'HM', iso3: 'HMD', name: 'Heard Island and McDonald Islands', dial: '' },
  { iso2: 'IM', iso3: 'IMN', name: 'Isle of Man', dial: '44' },
  { iso2: 'JE', iso3: 'JEY', name: 'Jersey', dial: '44' },
  { iso2: 'YT', iso3: 'MYT', name: 'Mayotte', dial: '262' },
  { iso2: 'PN', iso3: 'PCN', name: 'Pitcairn', dial: '' },
  { iso2: 'BL', iso3: 'BLM', name: 'Saint Barthélemy', dial: '590', aliases: ['Saint Barthelemy'] },
  { iso2: 'MF', iso3: 'MAF', name: 'Saint Martin (French part)', dial: '590' },
  { iso2: 'GS', iso3: 'SGS', name: 'South Georgia and the South Sandwich Islands', dial: '' },
  { iso2: 'SJ', iso3: 'SJM', name: 'Svalbard and Jan Mayen', dial: '47' },
  { iso2: 'UM', iso3: 'UMI', name: 'United States Minor Outlying Islands', dial: '' },
  { iso2: 'EH', iso3: 'ESH', name: 'Western Sahara', dial: '212' },
  { iso2: 'BQ', iso3: 'BES', name: 'Bonaire, Sint Eustatius and Saba', dial: '599' },
]

// ── Normalisation: case-fold + strip diacritics so "Türkiye" matches
// "turkiye", "Côte d'Ivoire" matches "cote d'ivoire", etc. ─────────────────
function norm(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // combining marks
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

// "Simplified" name: drop the part after a comma and any parenthetical, e.g.
// "Korea, Republic of" → "korea", "Taiwan, Province of China" → "taiwan".
function simplifyName(name: string): string {
  return norm(name.split(',')[0].replace(/\([^)]*\)/g, ''))
}

const BY_DIAL: Map<string, Country> = new Map()
const BY_ISO2: Map<string, Country> = new Map()
const BY_ISO3: Map<string, Country> = new Map()
const BY_NAME: Map<string, Country> = new Map()
// Candidate label → country, for fuzzy scoring. A simplified label that maps
// to MORE than one country is dropped (ambiguous, e.g. two "Korea" forms).
const FUZZY_CANDIDATES: Array<{ label: string; tokens: string[]; country: Country }> = []
const ambiguousSimple = new Set<string>()
const simpleSeen = new Map<string, Country>()
let MAX_DIAL_LEN = 1
for (const c of COUNTRIES) {
  if (c.dial && !BY_DIAL.has(c.dial)) BY_DIAL.set(c.dial, c) // first listed = primary for shared codes
  if (c.dial) MAX_DIAL_LEN = Math.max(MAX_DIAL_LEN, c.dial.length)
  BY_ISO2.set(c.iso2, c)
  BY_ISO3.set(c.iso3, c)
  BY_NAME.set(norm(c.name), c)
  for (const a of c.aliases || []) BY_NAME.set(norm(a), c)
  const labels = new Set<string>([norm(c.name), ...(c.aliases || []).map(norm)])
  const simple = simplifyName(c.name)
  if (simple) {
    const prev = simpleSeen.get(simple)
    if (prev && prev !== c) ambiguousSimple.add(simple)
    else simpleSeen.set(simple, c)
    labels.add(simple)
  }
  for (const label of labels) {
    if (label) FUZZY_CANDIDATES.push({ label, tokens: label.split(' ').filter(Boolean), country: c })
  }
}

// ── Fuzzy string similarity ───────────────────────────────────────────────
function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length
  if (!m) return n
  if (!n) return m
  let prev = new Array(n + 1)
  for (let j = 0; j <= n; j++) prev[j] = j
  for (let i = 1; i <= m; i++) {
    const cur = [i]
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost)
    }
    prev = cur
  }
  return prev[n]
}

// Similarity in [0,1] between a query and a candidate label.
function similarity(q: string, qTokens: string[], cand: string, candTokens: string[]): number {
  if (q === cand) return 1
  // Prefix relationship (one is the start of the other) — strong signal.
  if (cand.startsWith(q) || q.startsWith(cand)) {
    return 0.9 + 0.1 * (Math.min(q.length, cand.length) / Math.max(q.length, cand.length))
  }
  // Token overlap (Jaccard) — handles reordered / extra qualifier words.
  let inter = 0
  for (const t of qTokens) if (candTokens.includes(t)) inter++
  const union = new Set([...qTokens, ...candTokens]).size
  const jaccard = union ? inter / union : 0
  // Edit-distance ratio — handles typos.
  const dist = levenshtein(q, cand)
  const lev = 1 - dist / Math.max(q.length, cand.length)
  return Math.max(jaccard, lev)
}

/**
 * Best-effort fuzzy match of a free-typed country string. Returns the closest
 * country when confident enough, else undefined. Used as the fallback after
 * exact ISO / dial / name lookups fail.
 */
export function fuzzyMatchCountry(value: unknown): Country | undefined {
  if (value == null) return undefined
  const q = norm(String(value))
  if (q.length < 3) return undefined // too short to fuzzy-match safely
  const qTokens = q.split(' ').filter(Boolean)
  let best: Country | undefined
  let bestScore = 0
  let runnerUp = 0
  for (const cand of FUZZY_CANDIDATES) {
    // Skip ambiguous simplified labels (e.g. "korea" → two countries).
    if (ambiguousSimple.has(cand.label) && cand.label !== norm(cand.country.name)) continue
    const s = similarity(q, qTokens, cand.label, cand.tokens)
    if (s > bestScore) { runnerUp = bestScore; bestScore = s; best = cand.country }
    else if (s > runnerUp && cand.country !== best) { runnerUp = s }
  }
  // Confident enough, and clearly ahead of the next-best different country.
  if (best && bestScore >= 0.72 && (bestScore - runnerUp >= 0.06 || bestScore >= 0.92)) return best
  return undefined
}

/** Regional-indicator flag emoji for an ISO-3166-1 alpha-2 code, or '' if invalid. */
export function flagEmoji(iso2?: string | null): string {
  if (!iso2) return ''
  const cc = String(iso2).trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(cc)) return ''
  const A = 0x1f1e6
  return String.fromCodePoint(A + (cc.charCodeAt(0) - 65), A + (cc.charCodeAt(1) - 65))
}

/**
 * Resolve a stored country value to a Country. Accepts an ISO-2 ('IN'),
 * ISO-3 ('IND'), a dial code ('+91' / '91') or a country name in either the
 * official ISO form ('Russian Federation') or common form ('Russia').
 * Case- and diacritic-insensitive.
 */
export function resolveCountry(value: unknown): Country | undefined {
  if (value == null || value === '') return undefined
  const raw = String(value).trim()
  if (!raw) return undefined
  const upper = raw.toUpperCase()
  if (upper.length === 2 && BY_ISO2.has(upper)) return BY_ISO2.get(upper)
  if (upper.length === 3 && BY_ISO3.has(upper)) return BY_ISO3.get(upper)
  // Name (exact, then diacritic-folded).
  const byName = BY_NAME.get(norm(raw))
  if (byName) return byName
  // Bare dial code, with or without a leading '+'.
  if (/^\+?\d+$/.test(raw)) {
    const digits = raw.replace(/^\+/, '')
    if (BY_DIAL.has(digits)) return BY_DIAL.get(digits)
    return undefined // a number that isn't a known dial code — don't fuzzy-match
  }
  // Fuzzy fallback for slightly-off spellings / partial names.
  return fuzzyMatchCountry(raw)
}

/**
 * Identify the country of a phone number by its leading dial code. Only
 * matches when the number is in international form (starts with '+' or '00').
 * Returns the country with the longest matching dial prefix.
 */
export function countryFromPhone(value: unknown): Country | undefined {
  if (value == null) return undefined
  let s = String(value).trim()
  if (s.startsWith('00')) s = '+' + s.slice(2)
  if (!s.startsWith('+')) return undefined
  const digits = s.slice(1).replace(/[^0-9]/g, '')
  if (!digits) return undefined
  for (let len = Math.min(MAX_DIAL_LEN, digits.length); len >= 1; len--) {
    const prefix = digits.slice(0, len)
    const hit = BY_DIAL.get(prefix)
    if (hit) return hit
  }
  return undefined
}
